import { useMutation } from '@tanstack/react-query'
import AsyncSelect from 'react-select/async'
//API
import { useRef, useState } from 'react'
import { locationsAPI } from '../../../api/locationsAPI'
import { Location } from '../../../api/models/Common/Location'
import {
  FixedRouteSuggestion,
  PartialAddressSuggestion,
  PointOfInterestSuggestion,
} from '../../../api/models/GetAddress.type'
import ErrorMessage from '../ErrorMessage/ErrorMessage'

type AddressSearchProps = {
  onAddresssSelected: (selectedAddress: Location, selectedValue?: any) => void
  clientId?: number
  addressPlaceholder: string
  id?: string
  disabled?: boolean
  name: string
  styles?: object
  defaultValue?: { value: any; label: string }
  value?: { value: any; label: string } | null
  requirePostcode?: boolean
  onFocus?: () => void
  menuPortalTarget?: HTMLElement | null
  menuPosition?: 'absolute' | 'fixed'
  /** Suppress the dropdown until the input has at least this many characters. */
  minInputLength?: number
}

const AddressSearch = ({
  onAddresssSelected,
  clientId,
  addressPlaceholder,
  id,
  name,
  styles,
  disabled = false,
  defaultValue,
  value,
  requirePostcode = false,
  onFocus,
  menuPortalTarget,
  menuPosition,
  minInputLength,
}: AddressSearchProps) => {
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)
  const [postcodeError, setPostcodeError] = useState<string | null>(null)
  const [inputValue, setInputValue] = useState('')
  const mapAddressToLocation = (
    data: Location,
    formattedAddress: string,
  ): Location => {
    // Passing in formattedAddress due to geocoding api not returning a detailed address
    return {
      formattedAddress: formattedAddress,
      addressLine1: data.addressLine1,
      addressLine2: data.addressLine2,
      addressLine3: data.addressLine3,
      town: data.town,
      county: data.county,
      postCode: data.postCode,
      country: data.country,
      countryCode: data.countryCode,
      latitude: data.latitude,
      longitude: data.longitude,
    }
  }
  const { mutateAsync: autoComplete } = useMutation((inputValue: string) => {
    return locationsAPI.autoCompleteAddress(inputValue, clientId)
  })

  const { mutateAsync: getAddressById } = useMutation(
    (suggestion: PartialAddressSuggestion) => {
      return locationsAPI.getAddressById(suggestion)
    },
  )

  function isPartialAddressSuggestion(
    address: PartialAddressSuggestion | PointOfInterestSuggestion,
  ): address is PartialAddressSuggestion {
    return (address as PartialAddressSuggestion).id !== undefined
  }

  const getAddresses = async (inputValue: string) => {
    console.log('inputValue', inputValue)

    return await autoComplete(inputValue).then(
      (response: {
        data: {
          fixedRoutes: FixedRouteSuggestion[]
          pointsOfInterest: any[]
          partial: PartialAddressSuggestion[]
          standard: PartialAddressSuggestion[]
        }
      }) => {
        const results: any[] = []

        if (
          response?.data?.fixedRoutes &&
          response?.data?.fixedRoutes.length > 0 &&
          name === 'outbound.pickup'
        ) {
          results.push({
            label: 'Fixed Routes',
            options: response?.data?.fixedRoutes?.map(
              (f: FixedRouteSuggestion) => ({
                label: f.name,
                value: { fixedRoute: f },
              }),
            ),
          })
        }

        response?.data?.pointsOfInterest?.reduce<any[]>((acc, point) => {
          const name = point?.pointOfInterestTypeName
          const index = acc.findIndex((result) => result.label == name)
          const option = {
            label: point?.formattedAddress,
            value: { ...point },
          }
          if (index === -1) {
            acc.push({
              label: name,
              options: [option],
            })
          } else {
            acc[index].options.push(option)
          }
          return acc
        }, results)

        const partialOptions = (response?.data?.partial ?? []).map(
          (i: PartialAddressSuggestion) => ({
            label: i.formattedAddress,
            value: i,
          }),
        )
        const standardOptions = (response?.data?.standard ?? []).map(
          (s: PartialAddressSuggestion) => ({
            label: s.formattedAddress,
            value: s,
          }),
        )
        const addressOptions = [...partialOptions, ...standardOptions]

        if (addressOptions.length > 0) {
          results.push({
            options: addressOptions,
          })
        }

        return results
      },
    )
  }

  const getAddress = async (suggestion: PartialAddressSuggestion) => {
    return await getAddressById(suggestion).then((data) => {
      return data?.data
    })
  }

  const getOptions = (inputValue: string) => {
    if (inputValue.length > 2) {
      return new Promise((resolve) => {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current)
        }

        debounceTimerRef.current = setTimeout(() => {
          resolve(getAddresses(inputValue))
        }, 500)
      })
    }
    return Promise.resolve([])
  }

  const noPostcodeMessage =
    'This address has no postcode and cannot be used for a fixed route. Please select a more specific address.'

  const handleChange = async (option: any) => {
    setPostcodeError(null)
    if (isPartialAddressSuggestion(option?.value)) {
      const response: Location | undefined = await getAddress(option.value)
      if (response !== undefined) {
        if (requirePostcode && !response.postCode?.trim()) {
          setPostcodeError(noPostcodeMessage)
          return
        }
        const location = mapAddressToLocation(
          response,
          option.value.formattedAddress,
        )
        onAddresssSelected(location, option.value)
      }
    } else {
      if (requirePostcode && !option.value?.postCode?.trim()) {
        setPostcodeError(noPostcodeMessage)
        return
      }
      onAddresssSelected(option.value, option.value?.formattedAddress)
    }
  }

  return (
    <>
      <AsyncSelect
        defaultValue={defaultValue}
        id={id}
        onChange={handleChange}
        loadOptions={getOptions}
        placeholder={addressPlaceholder}
        isDisabled={disabled}
        styles={styles}
        value={value}
        onFocus={onFocus}
        menuPortalTarget={menuPortalTarget}
        menuPosition={menuPosition}
        onInputChange={(newValue) => setInputValue(newValue)}
        menuIsOpen={
          minInputLength != null
            ? inputValue.length >= minInputLength
            : undefined
        }
      />
      {postcodeError && (
        <ErrorMessage setFromResult={false} message={postcodeError} />
      )}
    </>
  )
}
export default AddressSearch
